import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { UserApi } from 'api';
import type { User } from 'api/methods/UserApi';

/**
 * [user] Query Key 생성
 * --
 */
export const userQueryKeys = createQueryKeys('user', {
  getUser: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getUsers: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [user] 회원 전체 조회
 * --
 */
export const useGetUsers = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: userQueryKeys.getUsers(params).queryKey,
    queryFn: () => UserApi.getUsers(params),
    ...options,
  });
  return data;
};

/**
 * [user] 회원 상세 조회
 * --
 */
export const useGetUser = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: userQueryKeys.getUser(idx, params).queryKey,
    queryFn: () => UserApi.getUser(idx, params),
  });

  return data;
};

/**
 * [user] 회원 생성
 * --
 */
export const useCreateUser = () => {
  const { mutateAsync: createUser, ...result } = useMutation({
    mutationFn: (variables: User) => UserApi.createUser(variables),
  });

  return { createUser, ...result };
};

/**
 * [user] 회원 수정
 * --
 */
export const useUpdateUser = () => {
  const { mutateAsync: updateUser, ...result } = useMutation({
    mutationFn: (variables: User) => UserApi.updateUser(variables),
  });

  return { updateUser, ...result };
};

/**
 * [user] 회원 삭제
 * --
 */
export const useDeleteUser = () => {
  const { mutateAsync: deleteUser, ...result } = useMutation({
    mutationFn: (variables: number) => UserApi.deleteUser(variables),
  });

  return { deleteUser, ...result };
};

/**
 * [user] 게스트 회원 수정
 * --
 */
export const useUpdateGuestUser = () => {
  const { mutateAsync: updateGuestUser, ...result } = useMutation({
    mutationFn: (variables: User) => UserApi.updateGuestUser(variables),
  });

  return { updateGuestUser, ...result };
};

/**
 * [user] 회원 일괄 수정
 * --
 */
export const useBulkUpsertUser = () => {
  const { mutateAsync: bulkUpsertUser, ...result } = useMutation({
    mutationFn: (variables: User[]) => UserApi.bulkUpsertUser(variables),
  });

  return { bulkUpsertUser, ...result };
};

/**
 * [user] 회원 등급 일괄 수정
 * --
 */
export const useBulkUpdateUserGrade = () => {
  const { mutateAsync: bulkUpdateUserGrade, ...result } = useMutation({
    mutationFn: (variables: User[]) => UserApi.bulkUpdateUserGrade(variables),
  });

  return { bulkUpdateUserGrade, ...result };
};
